import { useEffect, useState } from 'react'
import {
  fetchStats, fetchDaily, fetchDevices, fetchTimespent,
  type KPIData, type DailyRow, type DevicesResponse, type TimespentResponse,
} from '../../lib/api'
import { useDateRangeCtx } from '../DateRangeContext'
import { useFlowFilter } from '../FlowFilterContext'
import { DateRangeTabs } from '../DateRangeTabs'
import { KpiCard, pctDelta, ppDelta } from '../widgets/KpiCard'
import { DailyTrend } from '../widgets/DailyTrend'
import { DeviceBreakdown } from '../widgets/DeviceBreakdown'
import { TimeSpent } from '../widgets/TimeSpent'
import { Snapshots } from '../widgets/Snapshots'
import { AudienceComparison } from '../widgets/AudienceComparison'
import { LiveSessions } from '../widgets/LiveSessions'

const fmtPct = (n: number) => `${(n * 100).toFixed(1)}%`

export function OverviewSection() {
  const { range } = useDateRangeCtx()
  const { variantParam, audienceParam } = useFlowFilter()
  const [stats, setStats] = useState<KPIData | null>(null)
  const [daily, setDaily] = useState<DailyRow[] | null>(null)
  const [devices, setDevices] = useState<DevicesResponse | null>(null)
  const [timespent, setTimespent] = useState<TimespentResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true); setStats(null); setDaily(null); setDevices(null); setTimespent(null)
    Promise.all([
      fetchStats(range, variantParam, audienceParam).then((s) => !cancelled && setStats(s)),
      fetchDaily(range, variantParam, audienceParam).then((d) => !cancelled && setDaily(d.days)),
      fetchDevices(range, variantParam, audienceParam).then((d) => !cancelled && setDevices(d)),
      fetchTimespent(range, variantParam, audienceParam).then((t) => !cancelled && setTimespent(t)),
    ])
      .catch((err) => console.error('[overview]', err))
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [range.from, range.to, variantParam, audienceParam])

  return (
    <>
      <div className="section-toolbar">
        <DateRangeTabs />
      </div>

      <LiveSessions />

      <AudienceComparison range={range} />

      <div className="kpi-grid kpi-grid-4">
        <KpiCard
          label="Sessioner"
          value={stats?.sessions}
          delta={pctDelta(stats?.sessions, stats?.prev.sessions)}
          loading={loading}
        />
        <KpiCard
          label="Leads"
          value={stats?.leads}
          delta={pctDelta(stats?.leads, stats?.prev.leads)}
          loading={loading}
        />
        <KpiCard
          label="Konverteringsrate"
          value={stats ? fmtPct(stats.completionRate) : undefined}
          delta={ppDelta(stats?.completionRate, stats?.prev.completionRate)}
          loading={loading}
        />
        <KpiCard
          label="Leads sidste 24t"
          value={stats?.leadsLast24h}
          loading={loading}
        />
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-header">
            <div className="card-title">Daglig trend</div>
            <div className="card-subtitle">Sessioner og leads pr. dag</div>
          </div>
          <DailyTrend days={daily} loading={loading} />
        </div>
        <div className="card">
          <div className="card-header">
            <div className="card-title">Tidsforbrug</div>
            <div className="card-subtitle">Hvor lang tid bruger leads vs frafald</div>
          </div>
          <TimeSpent data={timespent} loading={loading} />
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Fordeling pr. enhed</div>
          <div className="card-subtitle">Hvor kommer trafik og leads fra</div>
        </div>
        <DeviceBreakdown data={devices} loading={loading} />
      </div>

      <Snapshots />
    </>
  )
}
